import {history, historyKeymap} from '@codemirror/commands'
import {type Command, EditorView, keymap, placeholder, type ViewUpdate} from '@codemirror/view'
import {readOnly} from '@github-ui/code-mirror/extensions/readonly'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {SuggestionOptions} from '@github-ui/markdown-editor/suggestions'
import type {Emoji} from '@github-ui/markdown-editor/suggestions/use-emoji-suggestions'
import type {Mentionable} from '@github-ui/markdown-editor/suggestions/use-mention-suggestions'
import type {Reference} from '@github-ui/markdown-editor/suggestions/use-reference-suggestions'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import type {FileType} from '@github-ui/use-unified-file-select'
import {PaperclipIcon} from '@primer/octicons-react'
import {ActionBar} from '@primer/react'
import {clsx} from 'clsx'
import {
  forwardRef,
  type MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import {useAutocomplete} from './autocomplete/use-autocomplete'
import {caretFlashExtension} from './codemirror/extensions/caret-flash'
import {dropPositionExtension} from './codemirror/extensions/drop-position'
import {focusChanged, isFocused} from './codemirror/extensions/focused'
import {markdownContinuationExtension} from './codemirror/extensions/markdown-continuation'
import {markdownKeymapExtension} from './codemirror/extensions/markdown-keymap'
import {isModPressedField, modPressedExtension} from './codemirror/extensions/mod-pressed'
import {pasteLinksExtension} from './codemirror/extensions/paste-links'
import {proseExtension} from './codemirror/extensions/prose'
import {
  getCurrentSelectionContext,
  initialSelectionContext,
  selectionContextChanged,
  selectionContextExtension,
} from './codemirror/extensions/selection-context'
import {singleLineEditorExtension} from './codemirror/extensions/single-line-editor'
import {styleContentsExtension} from './codemirror/extensions/style-contents'
import {uploadPlaceholderExtension} from './codemirror/extensions/upload-placeholder'
import {visitLinkExtension} from './codemirror/extensions/visit-link'
import {useCompartment} from './codemirror/hooks/use-compartment'
import {useReactWidgets} from './codemirror/hooks/use-react-widgets'
import {UploadPlaceholderWidget} from './codemirror/widgets/upload-placeholder'
import {SelectionContextProvider} from './context/SelectionContext'
import {ErrorFallback} from './ErrorFallback'
import {UploadPlaceholder} from './file-handler/UploadPlaceholder'
import {type FileUploadResult, useFileHandler} from './file-handler/useFileHandler'
import styles from './HybridMarkdownEditor.module.css'
import type {MarkdownViewMode, ViewSwitchTrigger} from './telemetry'
import {createHybridEditorTelemetry, HybridEditorTelemetryProvider, useHybridEditorTelemetry} from './telemetry'
import {Header} from './toolbar/Header'
import {Toolbar} from './toolbar/Toolbar'
import type {HybridMarkdownEditorRef} from './types'

// NOTE: These props should be a subset of `MarkdownEditorProps`, so that we can pass props through from the containing component
export interface HybridMarkdownEditorProps {
  'aria-describedby'?: string
  disabled?: boolean
  onChange?: (value: string) => void
  onChangeViewMode?: (newViewMode: MarkdownViewMode) => void
  placeholder?: string
  onUploadFile?: (file: File, onProgress?: (progressBytes: number) => void) => Promise<FileUploadResult>
  acceptedFileTypes?: FileType[]
  onFilesRejected?: (files: File[], rejectedTypes: string[]) => void
  value: string
  /**
   * Called when the user presses `Ctrl`/`Cmd` + `Enter`. Should always be wired to the same event as clicking the
   * primary action button in the footer.
   */
  onPrimaryAction?: () => void
  emojiTone?: number
  /** Array of all possible emojis to suggest. Leave `undefined` to disable emoji autocomplete. */
  emojiSuggestions?: SuggestionOptions<Emoji>
  /** Array of all possible mention suggestions. Leave `undefined` to disable `@`-mention autocomplete. */
  mentionSuggestions?: SuggestionOptions<Mentionable>
  /** Array of all possible references to suggest. Leave `undefined` to disable `#`-reference autocomplete. */
  referenceSuggestions?: SuggestionOptions<Reference>
  /** Called when the editor gains focus. */
  onInputFocus?: () => void
}

const DEFAULT_ACCEPTED_FILE_TYPES: FileType[] = ['image/*', 'video/*']

const InnerHybridMarkdownEditor = forwardRef<HybridMarkdownEditorRef, HybridMarkdownEditorProps>(
  function InnerHybridMarkdownEditor(
    {
      value,
      onChange,
      disabled,
      onChangeViewMode,
      placeholder: placeholderText,
      'aria-describedby': ariaDescribedBy,
      acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
      onUploadFile,
      onFilesRejected,
      onPrimaryAction,
      referenceSuggestions,
      mentionSuggestions,
      emojiSuggestions,
      emojiTone,
      onInputFocus,
    },
    ref,
  ) {
    const telemetry = useHybridEditorTelemetry()

    const viewModeRef = useRef<MarkdownViewMode>('hybrid')
    const [selectedViewMode, setSelectedViewMode] = useState<MarkdownViewMode>('hybrid')

    const emitViewModeChange = useCallback(
      (nextViewMode: MarkdownViewMode, trigger: ViewSwitchTrigger = 'view_switcher') => {
        const previousViewMode = viewModeRef.current
        telemetry.trackViewSwitch({from: previousViewMode, to: nextViewMode, trigger})
        viewModeRef.current = nextViewMode
        setSelectedViewMode(nextViewMode)
        onChangeViewMode?.(nextViewMode)
      },
      [onChangeViewMode, telemetry],
    )

    const handleViewSwitchChange = useCallback(
      (nextViewMode: MarkdownViewMode) => emitViewModeChange(nextViewMode, 'view_switcher'),
      [emitViewModeChange],
    )

    const [selectionContext, setSelectionContext] = useState(initialSelectionContext)

    /** Tracks the expected value for the next render according to user input. */
    const [expectedValue, setExpectedValue] = useState(value)

    /** Stable tracking reference to `onChange` so we don't have to update editor extensions when the prop changes. */
    const onChangeRef = useTrackingRef(onChange)
    const onInputFocusRef = useTrackingRef(onInputFocus)

    const onUpdate = useCallback(
      (update: ViewUpdate) => {
        if (update.docChanged) {
          const newValue = update.state.doc.toString()
          // As long as the consumer updates `value` state synchronously in its onChange handler, the next render will
          // have both `expectedValue` and `value` updated to the new value and will thus skip the reset step. If the
          // consumer ignores the update or changes `value` to something other than what was expected, we will reset
          // the editor, which will clear selection state. This 'synchronous update' requirement aligns with how React
          // handles regular DOM inputs (see https://react.dev/reference/react-dom/components/input#my-input-caret-jumps-to-the-beginning-on-every-keystroke).
          setExpectedValue(newValue)
          onChangeRef.current?.(newValue)
        }

        // Check for selection context changes
        if (update.transactions.some(tr => selectionContextChanged(tr))) {
          const newContext = getCurrentSelectionContext(update.state)
          setSelectionContext(newContext)
        }

        // Load in autocomplete suggestions on focus
        if (update.transactions.some(tr => focusChanged(tr))) {
          if (isFocused(update.state)) {
            onInputFocusRef.current?.()
          }
        }
      },
      [onChangeRef, onInputFocusRef],
    )

    const [view, setView] = useState<EditorView | null>(null)

    const {autocompleteSuggestionsMenu, autocompleteKeymapCompartment, autocompleteListenerExtension} = useAutocomplete(
      ref,
      view,
      referenceSuggestions,
      mentionSuggestions,
      emojiSuggestions,
      emojiTone,
      telemetry,
    )

    // Each extension that depends on prop values must be contained in a compartment to avoid reinitializing the editor
    // when the props change. Compartments are constant references so safe to use as dependencies.
    const placeholderCompartment = useCompartment(
      view,
      useMemo(() => placeholder(placeholderText ?? ''), [placeholderText]),
    )

    const readonlyCompartment = useCompartment(
      view,
      useMemo(() => (disabled ? readOnly() : []), [disabled]),
    )

    const a11yAttributesCompartment = useCompartment(
      view,
      useMemo(
        () =>
          EditorView.contentAttributes.of({
            'aria-label': 'Markdown content',
            ...(ariaDescribedBy ? {'aria-describedby': ariaDescribedBy} : {}),
          }),
        [ariaDescribedBy],
      ),
    )

    const onPrimaryActionRef = useTrackingRef(onPrimaryAction)
    const primaryAction: Command = useCallback(() => {
      if (onPrimaryActionRef.current) {
        onPrimaryActionRef.current()
        return true
      }
      return false
    }, [onPrimaryActionRef])

    const extensions = useMemo(
      () => [
        proseExtension,
        singleLineEditorExtension,
        styleContentsExtension,
        uploadPlaceholderExtension,
        dropPositionExtension,
        caretFlashExtension,
        selectionContextExtension,
        pasteLinksExtension,
        markdownContinuationExtension,
        visitLinkExtension(),
        history(),
        // ESLint false positive: we are accessing the ref value in a callback in response to an update, so it's safe
        // eslint-disable-next-line react-hooks/refs
        EditorView.updateListener.of(onUpdate),
        autocompleteListenerExtension,
        a11yAttributesCompartment,
        modPressedExtension,
        EditorView.contentAttributes.from(isModPressedField, isModPressed => ({
          // Note: Compartments appear to get applied after first render, which causes a flash of unstyled content when
          // `class` is applied inside a compartment. When we add support for a `className` prop, we'll want to keep
          // this line _in addition_ to the new compartment.
          class: clsx(styles.content, 'markdown-body', isModPressed && styles.modPressed),
        })),
        placeholderCompartment,
        readonlyCompartment,
        // eslint-disable-next-line react-hooks/refs
        markdownKeymapExtension({
          primaryAction,
          telemetry,
        }),
        autocompleteKeymapCompartment,
        keymap.of(historyKeymap),
      ],

      // IMPORTANT: These dependencies should not change during the lifecycle of the component
      [
        onUpdate,
        primaryAction,
        a11yAttributesCompartment,
        placeholderCompartment,
        readonlyCompartment,
        autocompleteKeymapCompartment,
        autocompleteListenerExtension,
        telemetry,
      ],
    )
    const containerRef = useRef<HTMLDivElement>(null)

    const fileHandler = useFileHandler(
      {
        value,
        view,
        disabled,
        onUploadFile,
        acceptedFileTypes,
        onFilesRejected,
      },
      telemetry,
    )

    const fileUploadButtonProps = useMemo(() => {
      const clickProps = fileHandler?.clickTargetProps
      if (!clickProps) return clickProps

      const originalOnClick = clickProps.onClick

      return {
        ...clickProps,
        onClick: (event: MouseEvent<HTMLButtonElement>) => {
          telemetry.trackToolbarInteraction({
            control: 'upload_file',
            action: 'upload_file',
            trigger: 'toolbar',
            isActive: false,
            handled: true,
          })

          originalOnClick?.(event)
        },
      }
    }, [fileHandler?.clickTargetProps, telemetry])
    const uploadPlaceholderWidgets = useReactWidgets(
      containerRef,
      UploadPlaceholderWidget.widgetName,
      UploadPlaceholder,
    )

    // Wrap drop target props to fix the dragleave issue when dragging over child elements
    const wrappedDropTargetProps = useMemo(() => {
      const dropProps = fileHandler?.dropTargetProps
      if (!dropProps) return undefined

      return {
        ...dropProps,
        onDragLeave: (event: React.DragEvent<HTMLDivElement>) => {
          // Only call the original handler if the drag is truly leaving the container
          // Check if the relatedTarget (where the drag is moving to) is outside the container
          if (!(event.currentTarget as Node).contains(event.relatedTarget as Node)) {
            dropProps.onDragLeave?.(event as unknown as React.DragEvent<HTMLDivElement>)
          }
        },
        onDragOver: dropProps.onDragOver,
        onDragEnter: dropProps.onDragEnter,
        onDrop: dropProps.onDrop,
      }
    }, [fileHandler?.dropTargetProps])

    const initialValueRef = useRef(value)
    useEffect(
      function initializeEditor() {
        const editorView = new EditorView({
          parent: containerRef.current!,
          doc: initialValueRef.current,
          extensions,
        })

        setView(editorView)

        return () => {
          editorView.destroy()
          setView(null)
        }
      },
      // IMPORTANT: These dependencies should not change during the lifecycle of the component
      [extensions],
    )

    useLayoutEffect(
      function syncControlledValue() {
        // If the value prop doesn't match the expected value, overwrite the input with the prop value
        // Only happens if `onChange` is defined, indicating that this is a controlled input
        if (expectedValue !== value && onChange !== undefined) {
          view?.update([view.state.update({changes: {from: 0, to: view.state.doc.length, insert: value}})])
        }
      },
      [expectedValue, value, onChange, view],
    )

    useImperativeHandle(
      ref,
      () => ({
        view,
        focus: () => view?.focus(),
        reset: () => view?.dispatch({changes: {from: 0, to: view.state.doc.length, insert: ''}}),
        scrollIntoView: opts => view?.dom?.scrollIntoView(opts),
        moveCursorToEnd: () => view?.dispatch({selection: {anchor: view.state.doc.length}}),
      }),
      [view],
    )

    return (
      <>
        <Header selectedView={selectedViewMode} onChangeViewMode={handleViewSwitchChange}>
          <SelectionContextProvider value={selectionContext}>
            <Toolbar
              view={view}
              fileUploadButton={
                onUploadFile ? (
                  <ActionBar.IconButton
                    variant="invisible"
                    icon={PaperclipIcon}
                    aria-label="Upload file"
                    {...fileUploadButtonProps}
                  />
                ) : null
              }
            />
          </SelectionContextProvider>
        </Header>

        {/* This is expected: https://www.a11yproject.com/posts/how-to-use-the-tabindex-attribute/#scrollable-overflow-containers */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <div className={styles.editorSurface} tabIndex={0} role="region" aria-label="Markdown editor content">
          <div
            ref={containerRef}
            className={fileHandler?.isDraggedOver ? styles.isDraggedOver : undefined}
            {...(wrappedDropTargetProps ?? {})}
            {...(fileHandler?.pasteTargetProps ?? {})}
          />
        </div>

        {autocompleteSuggestionsMenu}
        {uploadPlaceholderWidgets}
      </>
    )
  },
)

export const HybridMarkdownEditor = forwardRef<HybridMarkdownEditorRef, HybridMarkdownEditorProps>(
  // IMPORTANT: Keep this component as minimal as possible, with no logic that might possibly throw an error, since any
  // errors thrown in this component wouldn't be caught by the error boundary
  function HybridMarkdownEditor({onChangeViewMode, ...restProps}, ref) {
    const hybridEditorEnabled = isFeatureEnabled('markdown_experience_hybrid_editor')
    const telemetry = useMemo(
      () => createHybridEditorTelemetry({featureFlagEnabled: hybridEditorEnabled}),
      [hybridEditorEnabled],
    )

    return (
      <HybridEditorTelemetryProvider telemetry={telemetry}>
        <div className={styles.container}>
          <ErrorBoundary fallback={<ErrorFallback onChangeViewMode={onChangeViewMode} />}>
            <InnerHybridMarkdownEditor ref={ref} onChangeViewMode={onChangeViewMode} {...restProps} />
          </ErrorBoundary>
        </div>
      </HybridEditorTelemetryProvider>
    )
  },
)

try{ InnerHybridMarkdownEditor.displayName ||= 'InnerHybridMarkdownEditor' } catch {}
try{ InnerHybridMarkdownEditor.displayName ||= 'InnerHybridMarkdownEditor' } catch {}
try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}
try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}